/**
 * Buy Now Anchor Button
 * Shows a floating purchase shortcut until the real Add to Cart button enters
 * the viewport, then scrolls to Kaching or a merchant-selected target.
 */
(function () {
  'use strict';

  var BLOCK_SELECTOR = '[data-buy-now-anchor]';
  var ADD_TO_CART_SELECTORS = [
    '.main-product-atc',
    'product-form button[name="add"]',
    'form[action*="/cart/add"] button[type="submit"]',
  ];

  class BuyNowAnchor {
    constructor(blockElement) {
      this.blockElement = blockElement;
      this.buttonElement = blockElement.querySelector('[data-buy-now-anchor-button]');
      this.targetMode = blockElement.dataset.targetMode || 'default';
      this.customSelector = (blockElement.dataset.customTarget || '').trim();
      this.isFloating = blockElement.dataset.floating === 'true';
      this.addToCartButton = this.findAddToCartButton();
      this.visibilityObserver = null;
      this.formObserver = null;

      this.handleClick = this.handleClick.bind(this);
    }

    initialize() {
      if (!this.buttonElement || !this.addToCartButton) return false;

      this.blockElement.dataset.buyNowAnchorInitialized = 'true';
      this.watchAddToCartVisibility();
      this.watchForFormReplacement();
      this.buttonElement.addEventListener('click', this.handleClick);
      return true;
    }

    destroy() {
      this.buttonElement.removeEventListener('click', this.handleClick);
      if (this.visibilityObserver) this.visibilityObserver.disconnect();
      if (this.formObserver) this.formObserver.disconnect();
      delete this.blockElement.dataset.buyNowAnchorInitialized;
    }

    watchAddToCartVisibility() {
      if (!this.addToCartButton) return;

      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
      }

      this.visibilityObserver = new IntersectionObserver((entries) => {
        var report = entries[0];
        if (!report) return;

        if (report.isIntersecting) {
          this.hide();
          return;
        }

        if (report.boundingClientRect.top > 0) {
          this.show();
          return;
        }

        this.hide();
      }, { threshold: 0 });

      this.visibilityObserver.observe(this.addToCartButton);
    }

    watchForFormReplacement() {
      var productForm =
        this.addToCartButton.closest('product-form') || this.addToCartButton.closest('form');
      if (!productForm || !productForm.parentNode) return;

      this.formObserver = new MutationObserver(() => {
        var freshAddToCartButton = this.findAddToCartButton();
        if (!freshAddToCartButton || freshAddToCartButton === this.addToCartButton) return;

        this.addToCartButton = freshAddToCartButton;
        this.watchAddToCartVisibility();
      });

      this.formObserver.observe(productForm.parentNode, {
        childList: true,
        subtree: true,
      });
    }

    handleClick() {
      var headerOffset = this.getHeaderOffset();

      if (this.targetMode === 'custom') {
        this.scrollToCustomTarget(headerOffset);
        return;
      }

      this.scrollToDefaultTarget(headerOffset);
    }

    scrollToCustomTarget(headerOffset) {
      if (!this.customSelector) {
        console.warn('[buy-now-anchor] Custom mode but no target selector set.');
        if (this.isFloating) this.hide();
        return;
      }

      var customElement = null;
      try {
        var scope = this.blockElement.closest('quick-add-modal') || document;
        customElement = scope.querySelector(this.customSelector);
      } catch (error) {
        console.warn('[buy-now-anchor] Invalid custom selector:', this.customSelector, error);
      }

      if (customElement) this.scrollToElement(customElement, headerOffset);
      if (this.isFloating) this.hide();
    }

    scrollToDefaultTarget(headerOffset) {
      // Resolve at click time: experiment groups can hide either selector.
      var scope = this.getProductScope();
      var selectors = ['ext-custom-bundle', 'ext-mix-match', 'kaching-bundle', 'kaching-bundles-block', 'variant-radios', 'variant-selects'];
      var target = null;
      for (var selector of selectors) {
        target = Array.from(scope.querySelectorAll(selector)).find((element) => this.isRendered(element));
        if (target) break;
      }
      target = target || this.findAddToCartButton();
      if (target) this.scrollToElement(target, headerOffset);

      if (this.isFloating) this.hide();
    }

    findAddToCartButton() {
      var scope = this.getProductScope();
      for (var index = 0; index < ADD_TO_CART_SELECTORS.length; index += 1) {
        var element = Array.from(scope.querySelectorAll(ADD_TO_CART_SELECTORS[index]))
          .find((candidate) => this.isRendered(candidate));
        if (element) return element;
      }

      return null;
    }

    getProductScope() {
      return this.blockElement.closest('quick-add-modal') ||
        this.blockElement.closest('.shopify-section') || document;
    }

    isRendered(element) {
      if (!element || !element.getClientRects().length) return false;
      var style = getComputedStyle(element);
      return style.visibility !== 'hidden' && style.visibility !== 'collapse';
    }

    getHeaderOffset() {
      if (this.blockElement.closest('quick-add-modal')) return 0;
      var header = document.querySelector('sticky-header') || document.querySelector('.header-wrapper');
      return header ? header.offsetHeight : 0;
    }

    scrollToElement(element, headerOffset) {
      var modal = this.blockElement.closest('quick-add-modal');
      if (modal) {
        // Mobile scrolls the inner info panel; desktop scrolls the modal shell.
        // Move only that panel so the page behind the dialog stays in place.
        for (var scroller = element.parentElement; scroller && scroller !== modal; scroller = scroller.parentElement) {
          if (!/(auto|scroll)/.test(getComputedStyle(scroller).overflowY) ||
              scroller.scrollHeight <= scroller.clientHeight) continue;
          scroller.scrollTo({
            top: Math.max(0, scroller.scrollTop + element.getBoundingClientRect().top -
              scroller.getBoundingClientRect().top - scroller.clientTop - 12),
            behavior: 'smooth',
          });
          break;
        }
        return;
      }

      var pagePosition =
        element.getBoundingClientRect().top + window.scrollY - headerOffset;

      window.scrollTo({
        top: Math.max(0, pagePosition),
        behavior: 'smooth',
      });
    }

    show() {
      this.blockElement.classList.add('is-visible');
    }

    hide() {
      this.blockElement.classList.remove('is-visible');
    }
  }

  class BuyNowAnchorManager {
    constructor() {
      this.instances = new Map();
    }

    initialize(scope = document) {
      var blocks = [];
      if (scope.matches && scope.matches(BLOCK_SELECTOR)) blocks.push(scope);
      blocks.push(...scope.querySelectorAll(BLOCK_SELECTOR));

      blocks.forEach((block) => {
        if (block.dataset.buyNowAnchorInitialized === 'true') return;

        var anchor = new BuyNowAnchor(block);
        if (anchor.initialize()) this.instances.set(block, anchor);
      });
    }

    destroy(scope) {
      this.instances.forEach((anchor, block) => {
        if (block === scope || (scope.contains && scope.contains(block))) {
          anchor.destroy();
          this.instances.delete(block);
        }
      });
    }
  }

  var manager = new BuyNowAnchorManager();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => manager.initialize());
  } else {
    manager.initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    manager.initialize(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    manager.destroy(event.target);
  });
})();
